import React, { useState, useCallback, useEffect } from 'react';
import { X, User, Mail } from 'lucide-react';
import { useUser } from '../../../contexts/UserContext';

import { logger } from '@/utils/logger';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
}

/**
 * LoginModal - Magic link authentication modal
 * 
 * Features:
 * - Sends magic link via email
 * - Auto-closes when login succeeds in another tab
 * - Focuses window when login succeeds via BroadcastChannel
 * - Invite-only authentication (shouldCreateUser: false)
 */
const LoginModal: React.FC<LoginModalProps> = React.memo(({
  isOpen,
  onClose,
  isLoading = false
}) => {
  const { signInWithMagicLink } = useUser();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  /**
   * Listen for successful login in new tab from magic link
   * When login succeeds in NEW tab, this ORIGINAL tab (with login modal) should close
   * This allows the user to remain on the dashboard in the new tab
   */
  useEffect(() => {
    const channel = new BroadcastChannel('auth-events');
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'MAGIC_LINK_SUCCESS' && event.data?.closeOriginalTab) {
        if (import.meta.env.DEV) logger.info('[LoginModal] Magic link login succeeded in new tab, closing original tab with login modal');
        setMagicLinkSent(false);
        onClose();
        
        // Close this tab (original tab with login modal) so user remains in new tab with dashboard
        // Only close if we can't be the user's only window
        try {
          // Check if we can safely close (not the last remaining window)
          if (window.opener || window.history.length > 1 || document.referrer) {
            // Use setTimeout to ensure message processing completes before closing
            setTimeout(() => {
              window.close();
            }, 100);
          } else {
            // If we can't close, at least navigate away from login
            window.location.replace('/');
          }
        } catch (e) {
          if (import.meta.env.DEV) logger.warn('[LoginModal] Could not close tab, navigating instead:', e);
          window.location.replace('/');
        }
      }
    };
    
    channel.addEventListener('message', handler);
    
    const windowHandler = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.data?.type === 'MAGIC_LINK_SUCCESS' && event.data?.closeOriginalTab) {
        if (import.meta.env.DEV) logger.info('[LoginModal] Magic link login succeeded via postMessage, closing original tab');
        setMagicLinkSent(false);
        onClose();
        
        // Close this tab (original tab with login modal)
        try {
          if (window.opener || window.history.length > 1 || document.referrer) {
            setTimeout(() => {
              window.close();
            }, 100);
          } else {
            window.location.replace('/');
          }
        } catch (e) {
          if (import.meta.env.DEV) logger.warn('[LoginModal] Could not close tab, navigating instead:', e);
          window.location.replace('/');
        }
      }
    };
    
    window.addEventListener('message', windowHandler);
    
    return () => {
      channel.removeEventListener('message', handler);
      window.removeEventListener('message', windowHandler);
      channel.close();
    };
  }, [onClose]);

  /**
   * Reset form state when modal opens/closes
   */
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setError('');
      setMagicLinkSent(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  /**
   * Handle magic link request submission
   * Uses invite-only authentication (shouldCreateUser: false)
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { error } = await signInWithMagicLink(email.trim());
      
      if (error) {
        if (error.message?.includes('User not found') || 
            error.message?.includes('Invalid login') ||
            error.message?.includes('Email not confirmed') ||
            error.message?.includes('signup_disabled') ||
            error.message?.includes('Signups not allowed')) {
          setError('Contact admin for approval');
        } else {
          setError(error.message || 'Failed to send magic link');
        }
      } else {
        setMagicLinkSent(true);
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [email, signInWithMagicLink]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setEmail('');
      setError('');
      setMagicLinkSent(false);
      onClose();
    }
  }, [isSubmitting, onClose]);

  // Prevent flash: Only render if explicitly opened
  // This avoids brief appearance during page load
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200"
      style={{ 
        backgroundColor: 'var(--modal-backdrop)',
        // Prevent flash during initial render
        animation: 'fadeIn 0.2s ease-in'
      }}
    >
      <div className="relative w-full max-w-sm theme-surface rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-6 border-b theme-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-[color-mix(in_srgb,var(--accent-primary) 12%, var(--bg) 88%)]">
              <Mail className="w-6 h-6 text-[var(--accent-primary)]" />
            </div>
            <div>
              <h2 className="text-xl font-semibold theme-text">Sign In</h2>
              <p className="text-sm theme-text-muted">Enter your email to receive a magic link</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1 theme-text-muted hover:theme-text-muted transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {magicLinkSent && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-600">
                Magic link sent to {email}. Please check your email and click the link to sign in.
              </p>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium theme-text mb-2">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 theme-text-muted" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                className="block w-full pl-10 pr-3 py-2 border theme-border rounded-md theme-surface theme-text placeholder-theme-text-muted shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:border-[var(--accent-primary)] disabled:theme-muted disabled:theme-text-muted"
                placeholder="Enter your email"
                autoComplete="email"
                autoFocus
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="w-full flex justify-center py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-[var(--accent-primary)] hover:bg-[color-mix(in_srgb,var(--accent-primary) 85%, #ffffff 15%)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--ring)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sending...</span>
              </div>
            ) : (
              'Send Magic Link'
            )}
          </button>
        </form>
      </div>
    </div>
  );
});

LoginModal.displayName = 'LoginModal';

export default LoginModal; 